import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export async function generatePdfFromHtml(htmlBlocks, filename) {
  if (typeof htmlBlocks === "string") {
    htmlBlocks = [htmlBlocks];
  }

  const PAGE_WIDTH = 210;
  const PAGE_HEIGHT = 297;
  const MARGIN = 15;
  const FOOTER_MARGIN = 15;

  const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
  const CONTENT_HEIGHT = PAGE_HEIGHT - MARGIN - FOOTER_MARGIN;

  // --------------------------------------------------
  // Create hidden container
  // --------------------------------------------------
  const createContainer = () => {
    const container = document.createElement("div");

    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";

    container.style.width = "750px";
    container.style.boxSizing = "border-box";

    container.style.padding = "40px";

    container.style.margin = "0";

    container.style.background = "#ffffff";

    container.style.fontFamily =
      "'Noto Sans', 'Noto Sans Devanagari', 'Noto Sans Gujarati', sans-serif";

    container.style.fontSize = "14px";
    container.style.lineHeight = "1.6";
    container.style.color = "#28283c";

    return container;
  };

  // --------------------------------------------------
  // Wait for fonts/layout
  // --------------------------------------------------
  const waitForStableLayout = async (container) => {
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (e) { }
    }

    const imgs = Array.from(container.querySelectorAll("img"));
    if (imgs.length > 0) {
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) return resolve();
              img.onload = () => resolve();
              img.onerror = () => resolve();
            })
        )
      );
    }

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    void container.offsetHeight;
  };

  // --------------------------------------------------
  // Decimal check
  // --------------------------------------------------
  const isDecimalPoint = (text, index) => {
    const before = text[index - 1];
    const after = text[index + 1];

    return (
      /\d/.test(before || "") &&
      /\d/.test(after || "")
    );
  };

  // --------------------------------------------------
  // Get all text nodes
  //
  // We create one logical text string from all text
  // nodes so sentences can also cross HTML tags.
  // IMPORTANT: When crossing block-element boundaries,
  // insert a newline so the sentence regex recognizes
  // the boundary (prevents fusing "...ends here.Next para"
  // into one giant sentence when </p><p> has no whitespace).
  // --------------------------------------------------
  const getTextNodes = (container) => {
    const nodes = [];
    const BLOCK_TAGS = new Set([
      "P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6",
      "LI", "TR", "TD", "TH", "BLOCKQUOTE", "PRE",
      "UL", "OL", "DL", "SECTION", "ARTICLE", "HEADER", "FOOTER"
    ]);

    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (!node.nodeValue) {
            return NodeFilter.FILTER_REJECT;
          }

          if (!node.nodeValue.trim()) {
            return NodeFilter.FILTER_ACCEPT;
          }

          const tag =
            node.parentElement?.tagName?.toUpperCase();

          if (
            tag === "SCRIPT" ||
            tag === "STYLE"
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        },
      }
    );

    let node;
    let offset = 0;
    let prevBlockParent = null;

    while ((node = walker.nextNode())) {
      const text = node.nodeValue || "";

      // Find nearest block-level ancestor
      let blockParent = node.parentElement;
      while (blockParent && blockParent !== container) {
        if (BLOCK_TAGS.has(blockParent.tagName?.toUpperCase())) {
          break;
        }
        blockParent = blockParent.parentElement;
      }

      // If crossing from one block to another, insert synthetic newline
      const gapBefore =
        !!prevBlockParent &&
        !!blockParent &&
        prevBlockParent !== blockParent;

      if (gapBefore) {
        offset += 1; // space for the synthetic "\n"
      }

      nodes.push({
        node,
        start: offset,
        end: offset + text.length,
        gapBefore,
      });

      offset += text.length;
      prevBlockParent = blockParent;
    }

    return {
      nodes,
      totalLength: offset,
    };
  };

  // --------------------------------------------------
  // Convert global text index -> DOM position
  // --------------------------------------------------
  const getDomPosition = (
    textNodes,
    globalIndex
  ) => {
    for (const item of textNodes) {
      if (
        globalIndex >= item.start &&
        globalIndex <= item.end
      ) {
        return {
          node: item.node,
          offset: Math.max(
            0,
            Math.min(
              globalIndex - item.start,
              item.node.nodeValue.length
            )
          ),
        };
      }
    }

    const last =
      textNodes[textNodes.length - 1];

    if (!last) {
      return null;
    }

    return {
      node: last.node,
      offset: last.node.nodeValue.length,
    };
  };

  // --------------------------------------------------
  // Get complete text
  // --------------------------------------------------
  const getFullText = (textNodes) => {
    return textNodes
      .map((item) =>
        (item.gapBefore ? "\n" : "") + (item.node.nodeValue || "")
      )
      .join("");
  };

  // --------------------------------------------------
  // Get regions explicitly marked with
  // data-pdf-keep-together="1". Used for small multi-line
  // groups (e.g. a transcript chunk: speaker label +
  // original text + translated text) that must never be
  // split across a page even though they span more than
  // one sentence/line.
  // --------------------------------------------------
  const collectKeepTogetherRegions = (container) => {
    const containerRect =
      container.getBoundingClientRect();

    const elements = Array.from(
      container.querySelectorAll('[data-pdf-keep-together]')
    );

    const regions = [];

    elements.forEach((el) => {
      const rect =
        el.getBoundingClientRect();

      if (!rect.height) {
        return;
      }

      regions.push({
        start: rect.top - containerRect.top,
        end: rect.bottom - containerRect.top,
      });
    });

    return regions;
  };

  // --------------------------------------------------
  // Get sentence information
  //
  // IMPORTANT:
  //
  // We store:
  //
  // sentenceStartLineTop
  //
  // instead of simply using sentence-ending
  // punctuation.
  //
  // This prevents cutting a line between two sentences.
  // --------------------------------------------------
  const collectSentences = (
    container,
    textNodes
  ) => {
    const containerRect =
      container.getBoundingClientRect();

    const fullText =
      getFullText(textNodes);

    const sentenceRegex =
      /[.!?।॥]+[)\]"'”’]*(?=\s|$)/g;

    const sentences = [];

    let match;

    while (
      (match = sentenceRegex.exec(fullText)) !== null
    ) {
      const punctuationIndex =
        match.index;

      const punctuationText =
        match[0];

      // Ignore decimal values such as:
      // 12.50
      if (
        punctuationText[0] === "." &&
        isDecimalPoint(
          fullText,
          punctuationIndex
        )
      ) {
        continue;
      }

      const sentenceEnd =
        punctuationIndex +
        punctuationText.length;

      // ------------------------------------------------
      // Find sentence start.
      // ------------------------------------------------
      let sentenceStart =
        punctuationIndex - 1;

      while (sentenceStart >= 0) {
        const char =
          fullText[sentenceStart];

        if (
          char === "." ||
          char === "!" ||
          char === "?" ||
          char === "।" ||
          char === "॥" ||
          char === "\n"
        ) {
          sentenceStart++;
          break;
        }

        sentenceStart--;
      }

      if (sentenceStart < 0) {
        sentenceStart = 0;
      }

      // Remove leading whitespace.
      while (
        sentenceStart < punctuationIndex &&
        /\s/.test(
          fullText[sentenceStart]
        )
      ) {
        sentenceStart++;
      }

      const startPosition =
        getDomPosition(
          textNodes,
          sentenceStart
        );

      const endPosition =
        getDomPosition(
          textNodes,
          sentenceEnd
        );

      if (
        !startPosition ||
        !endPosition
      ) {
        continue;
      }

      try {
        const range =
          document.createRange();

        range.setStart(
          startPosition.node,
          startPosition.offset
        );

        range.setEnd(
          endPosition.node,
          endPosition.offset
        );

        const rects = Array.from(
          range.getClientRects()
        );

        if (!rects.length) {
          continue;
        }

        const firstRect = rects[0];
        const lastRect =
          rects[rects.length - 1];

        // ------------------------------------------------
        // FIRST LINE of sentence
        // ------------------------------------------------
        const sentenceStartY =
          firstRect.top -
          containerRect.top;

        // ------------------------------------------------
        // LAST LINE of sentence
        // ------------------------------------------------
        const sentenceEndY =
          lastRect.bottom -
          containerRect.top;

        sentences.push({
          start: sentenceStartY,
          end: sentenceEndY,

          // Start of the first rendered line
          lineTop: sentenceStartY,

          // End of the last rendered line
          lineBottom: sentenceEndY,
        });
      } catch (e) {
        // Ignore invalid ranges
      }
    }

    return sentences.sort(
      (a, b) => a.start - b.start
    );
  };

  // --------------------------------------------------
  // Collect all rendered LINE TOP positions.
  //
  // We gather the vertical top of every distinct text
  // line box and block element in the container.
  // We cluster tops within 4px to ensure any cut is
  // cleanly above all glyphs and inline spans on that line.
  // Using them guarantees we NEVER slice through the middle
  // of a line of text.
  // --------------------------------------------------
  const collectLineTops = (container) => {
    const containerTop =
      container.getBoundingClientRect().top;

    const tops = new Set();

    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (
            node.nodeValue &&
            node.nodeValue.trim()
          ) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        },
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      try {
        const range =
          document.createRange();
        range.selectNodeContents(node);
        const rects = Array.from(
          range.getClientRects()
        );
        for (const rect of rects) {
          if (rect.height <= 0) continue;
          tops.add(
            Math.round((rect.top - containerTop) * 100) / 100
          );
        }
        range.detach();
      } catch (e) {
        // ignore
      }
    }

    // Also collect tops of block elements (headings, paragraphs, pre, tables, images, hr)
    const blockElements = container.querySelectorAll(
      "h1, h2, h3, h4, h5, h6, p, pre, table, tr, img, hr, blockquote, ul, ol, li, div"
    );
    for (const el of blockElements) {
      const rect = el.getBoundingClientRect();
      if (rect.height > 0) {
        tops.add(Math.round((rect.top - containerTop) * 100) / 100);
      }
    }

    // Sort and cluster tops that belong to the same text line (within 4px)
    // taking the minimum top so any cut is cleanly above the entire line
    const rawTops = Array.from(tops).sort((a, b) => a - b);
    const clusteredTops = [];
    for (const top of rawTops) {
      if (
        clusteredTops.length === 0 ||
        top - clusteredTops[clusteredTops.length - 1] > 4
      ) {
        clusteredTops.push(top);
      }
    }

    return clusteredTops;
  };

  // --------------------------------------------------
  // Find safe cut
  //
  // Guarantees:
  // 1. If a sentence doesn't fit at the bottom of the page,
  //    the WHOLE sentence moves to the next page.
  // 2. Keep-together regions (e.g. images, tables) are never sliced.
  // 3. Cuts always occur at clean line boundaries (lineTop),
  //    so text glyphs and words are never cut in half.
  // 4. Proper footer space is preserved on all pages.
  // --------------------------------------------------
  const findSafeCut = (
    sentences,
    keepTogetherRegions,
    lineTopsMm,
    startMm,
    pageHeightMm,
    totalMm
  ) => {
    const EPS = 0.5;

    const pageLimit = Math.min(
      startMm + pageHeightMm,
      totalMm
    );

    const crossesKeepTogether = (y) =>
      (keepTogetherRegions || []).some(
        (region) =>
          y - region.start > EPS &&
          region.end - y > EPS
      );

    // Check if a Y position falls inside any sentence's vertical range
    const crossesSentence = (y) =>
      (sentences || []).some(
        (s) => y - s.start > EPS && s.end - y > EPS
      );

    // 1. If a keep-together element crosses pageLimit, cut before it so it starts on next page
    for (const region of keepTogetherRegions || []) {
      if (
        region.start > startMm + EPS &&
        region.start < pageLimit &&
        region.end > pageLimit
      ) {
        return region.start;
      }
    }

    // 2. If a sentence starts on this page and does NOT completely fit (its end crosses pageLimit),
    // move the entire sentence to the next page by cutting before it (at s.start / lineTop).
    for (const s of sentences || []) {
      if (
        s.start > startMm + EPS &&
        s.start < pageLimit &&
        s.end > pageLimit
      ) {
        // If s.start is inside a keep-together region that started on this page,
        // cut before the keep-together region instead.
        const parentRegion = (keepTogetherRegions || []).find(
          (r) => r.start > startMm + EPS && r.start <= s.start && r.end >= s.end
        );
        if (parentRegion && parentRegion.start > startMm + EPS) {
          return parentRegion.start;
        }

        if (!crossesKeepTogether(s.start)) {
          return s.start;
        }
      }
    }

    // 3. Otherwise, find the best rendered LINE TOP <= pageLimit that does not cut inside
    // a keep-together element AND does not split a sentence across pages.
    let bestCleanLineCut = null;
    for (const top of lineTopsMm || []) {
      if (
        top > startMm + EPS &&
        top <= pageLimit &&
        !crossesKeepTogether(top) &&
        !crossesSentence(top)
      ) {
        if (bestCleanLineCut === null || top > bestCleanLineCut) {
          bestCleanLineCut = top;
        }
      }
    }

    if (bestCleanLineCut !== null) {
      return bestCleanLineCut;
    }

    // 4. Cut before the last sentence that starts on this page
    for (let i = (sentences || []).length - 1; i >= 0; i--) {
      const s = sentences[i];
      if (s.start > startMm + EPS && s.start <= pageLimit && !crossesKeepTogether(s.start)) {
        return s.start;
      }
    }

    // 5. Fallback: cut at the latest line boundary <= pageLimit (not crossing keepTogether)
    let bestAnyLineCut = null;
    for (const top of lineTopsMm || []) {
      if (top > startMm + EPS && top <= pageLimit && !crossesKeepTogether(top)) {
        if (bestAnyLineCut === null || top > bestAnyLineCut) {
          bestAnyLineCut = top;
        }
      }
    }

    if (bestAnyLineCut !== null) {
      return bestAnyLineCut;
    }

    // 6. Absolute line fallback: cut at ANY line boundary <= pageLimit
    // to guarantee we NEVER slice through the middle of letters/words.
    let absoluteLineCut = null;
    for (const top of lineTopsMm || []) {
      if (top > startMm + EPS && top <= pageLimit) {
        if (absoluteLineCut === null || top > absoluteLineCut) {
          absoluteLineCut = top;
        }
      }
    }

    if (absoluteLineCut !== null) {
      return absoluteLineCut;
    }

    return null;
  };

  // --------------------------------------------------
  // Render canvas slice
  // --------------------------------------------------
  const renderSlice = ({
    pdf,
    canvas,
    startMm,
    endMm,
    offsetY = 0,
  }) => {
    const totalMm =
      (canvas.height / canvas.width) *
      CONTENT_WIDTH;

    const SLICE_OVERLAP_PX = 0;

    const startPx = Math.max(
      0,
      Math.round(
        (startMm / totalMm) * canvas.height
      )
    );

    const rawEndPx = Math.round(
      (endMm / totalMm) * canvas.height
    );

    const endPx = Math.min(
      canvas.height,
      rawEndPx + SLICE_OVERLAP_PX
    );

    const sliceHeightPx = Math.max(
      1,
      endPx - startPx
    );

    const sliceCanvas =
      document.createElement("canvas");

    sliceCanvas.width =
      canvas.width;

    sliceCanvas.height =
      sliceHeightPx;

    const ctx =
      sliceCanvas.getContext("2d");

    if (!ctx) {
      return 0;
    }

    ctx.drawImage(
      canvas,

      // Source
      0,
      startPx,
      canvas.width,
      sliceHeightPx,

      // Destination
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const renderedHeight =
      (sliceHeightPx / canvas.height) *
      totalMm;

    pdf.addImage(
      sliceCanvas,
      "JPEG",
      MARGIN,
      MARGIN + offsetY,
      CONTENT_WIDTH,
      renderedHeight,
      undefined,
      "FAST"
    );

    return offsetY + renderedHeight;
  };

  // --------------------------------------------------
  // PDF
  // --------------------------------------------------
  const pdf = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  let currentY = 0;

  // ==================================================
  // PROCESS BLOCKS
  // ==================================================
  for (const blockHtml of htmlBlocks) {
    const container =
      createContainer();

    container.innerHTML =
      blockHtml;

    document.body.appendChild(
      container
    );

    await waitForStableLayout(
      container
    );

    const cssHeight =
      container.getBoundingClientRect()
        .height;

    if (!cssHeight) {
      document.body.removeChild(
        container
      );
      continue;
    }

    // ------------------------------------------------
    // Get text nodes + sentences BEFORE rendering
    // ------------------------------------------------
    const {
      nodes: textNodes,
    } = getTextNodes(container);

    const sentences =
      collectSentences(
        container,
        textNodes
      );

    const keepTogetherRegionsCss =
      collectKeepTogetherRegions(container);

    const lineTopsCss =
      collectLineTops(container);

    // ------------------------------------------------
    // Render ONCE
    // ------------------------------------------------
    const canvas =
      await html2canvas(
        container,
        {
          scale: 1.5,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          removeContainer: true,
        }
      );

    document.body.removeChild(
      container
    );

    // ------------------------------------------------
    // Image dimensions
    // ------------------------------------------------
    const imageHeight =
      (canvas.height *
        CONTENT_WIDTH) /
      canvas.width;

    if (imageHeight <= 0) {
      continue;
    }

    // ------------------------------------------------
    // Convert Y positions:
    //
    // CSS px -> PDF mm
    // ------------------------------------------------
    const mmPerCssPx =
      imageHeight / cssHeight;

    const sentenceMm =
      sentences.map(
        (sentence) => ({
          start:
            sentence.start *
            mmPerCssPx,

          end:
            sentence.end *
            mmPerCssPx,

          lineTop:
            sentence.lineTop *
            mmPerCssPx,

          lineBottom:
            sentence.lineBottom *
            mmPerCssPx,
        })
      );

    const keepTogetherMm =
      keepTogetherRegionsCss.map(
        (region) => ({
          start:
            region.start *
            mmPerCssPx,

          end:
            region.end *
            mmPerCssPx,
        })
      );

    const lineTopsMm =
      lineTopsCss.map(
        (top) => top * mmPerCssPx
      );

    // ==================================================
    // BLOCK FITS CURRENT PAGE COMPLETELY
    // ==================================================
    if (
      imageHeight <=
      CONTENT_HEIGHT -
      currentY +
      0.1
    ) {
      pdf.addImage(
        canvas,
        "JPEG",
        MARGIN,
        MARGIN + currentY,
        CONTENT_WIDTH,
        imageHeight,
        undefined,
        "FAST"
      );

      currentY += imageHeight;

      continue;
    }

    // ==================================================
    // IF CURRENT PAGE HAS SPACE, START BLOCK ON CURRENT PAGE FIRST
    // ==================================================
    let sliceStartMm = 0;

    if (currentY > 0.1) {
      const pageAvail = CONTENT_HEIGHT - currentY;
      const initialCutMm =
        pageAvail >= 15
          ? findSafeCut(
            sentenceMm,
            keepTogetherMm,
            lineTopsMm,
            0,
            pageAvail,
            imageHeight
          )
          : null;

      if (initialCutMm !== null && initialCutMm > 0.5) {
        renderSlice({
          pdf,
          canvas,
          startMm: 0,
          endMm: initialCutMm,
          offsetY: currentY,
        });

        sliceStartMm = initialCutMm;
        pdf.addPage();
        currentY = 0;
      } else {
        pdf.addPage();
        currentY = 0;
      }
    }

    // ==================================================
    // REMAINING PORTION FITS FRESH PAGE
    // ==================================================
    const remainingMm = imageHeight - sliceStartMm;

    if (
      remainingMm <=
      CONTENT_HEIGHT + 0.1
    ) {
      if (sliceStartMm === 0) {
        pdf.addImage(
          canvas,
          "JPEG",
          MARGIN,
          MARGIN,
          CONTENT_WIDTH,
          imageHeight,
          undefined,
          "FAST"
        );
        currentY = imageHeight;
      } else {
        currentY = renderSlice({
          pdf,
          canvas,
          startMm: sliceStartMm,
          endMm: imageHeight,
          offsetY: 0,
        });
      }
      continue;
    }

    // ==================================================
    // BLOCK IS LONGER THAN ONE PAGE (SLICE ACROSS PAGES)
    // ==================================================
    while (
      sliceStartMm <
      imageHeight - 0.05
    ) {
      const remaining =
        imageHeight -
        sliceStartMm;

      // ------------------------------------------------
      // Remaining content fits completely
      // ------------------------------------------------
      if (
        remaining <=
        CONTENT_HEIGHT + 0.1
      ) {
        currentY =
          renderSlice({
            pdf,
            canvas,
            startMm:
              sliceStartMm,
            endMm:
              imageHeight,
            offsetY: 0,
          });

        break;
      }

      // ------------------------------------------------
      // Find safe page cut
      // ------------------------------------------------
      let cutMm =
        findSafeCut(
          sentenceMm,
          keepTogetherMm,
          lineTopsMm,
          sliceStartMm,
          CONTENT_HEIGHT,
          imageHeight
        );

      if (cutMm !== null && cutMm > sliceStartMm + 0.5) {
        currentY =
          renderSlice({
            pdf,
            canvas,
            startMm:
              sliceStartMm,
            endMm:
              cutMm,
            offsetY: 0,
          });

        sliceStartMm =
          cutMm;

        pdf.addPage();
        currentY = 0;
        continue;
      }

      // Fallback: hard limit if cutMm was null or too small
      const hardEndMm = Math.min(
        sliceStartMm + CONTENT_HEIGHT,
        imageHeight
      );

      currentY =
        renderSlice({
          pdf,
          canvas,
          startMm:
            sliceStartMm,
          endMm:
            hardEndMm,
          offsetY: 0,
        });

      sliceStartMm =
        hardEndMm;

      if (
        sliceStartMm <
        imageHeight - 0.05
      ) {
        pdf.addPage();
      }
    }
  }

  // ==================================================
  // SAVE
  // ==================================================
  pdf.save(filename);
}
